import { FeedPositionCardWithType } from '@flipster/apis/v1/community/types';
import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles, css, cx } from '@flipster/styled-system/css';
import { circle, divider, flex, hstack, stack } from '@flipster/styled-system/patterns';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { LeverageBadge, OrderSideBadge } from '../../../misc/badge';
import { Pnl } from '../../pnl';
import { useRawSnapshotSpec } from '../../snapshot/use-snapshot-spec';

interface Props {
  type: 'VIEW' | 'EDIT';
  item: FeedPositionCardWithType;
  css?: Styles;
  onClick?: (symbol: string) => void;
}

export const FeedPositionCard = ({ type, item, css: cssProps, onClick }: Props) => {
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();
  const card = item.positionCard;
  const { isMobile } = useBreakpoint();
  const { pricePrecision, quoteCurrency, positionUnit } = instrumentsKeyBySymbol?.[card.symbol] ?? {};
  const { positionSize, price, priceLabel, timestamp, realizedPnl, realizedPnlPct, unrealizedPnl, unrealizedPnlPct } =
    getPositionCardData(item);
  const quotePrecision = getQuoteCurrencyPrecision(quoteCurrency);
  return (
    <div
      role='button'
      tabIndex={0}
      className={cx(
        'position-card',
        css(
          stack.raw({
            p: 12,
            rounded: 8,
            bg: 'background.default.level-1',
            gap: 4,
            cursor: onClick ? 'pointer' : 'auto',
          }),
          cssProps,
        ),
      )}
      onClick={() => {
        if (!isMobile) onClick?.(card.symbol);
      }}
    >
      <div className={stack({ gap: 12 })}>
        <div className={hstack({ justifyContent: 'space-between' })}>
          <div className={hstack({ gap: 4 })}>
            <OrderSideBadge orderSide={card.side} />
            <LeverageBadge leverage={card.initLeverage} />
          </div>
          {item.positionCardType !== 'CLOSED' && (
            <div className={hstack({ gap: 5, textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
              <div className={circle({ size: 8, bg: 'background.accent.buy' })} />
              <span>
                <FormattedMessage id='Autogen_common.r251126.s_Open' />
              </span>
            </div>
          )}
        </div>
        <div
          className={css(
            { gap: 12 },
            type === 'EDIT' ? stack.raw() : flex.raw({ smallDesktopDown: { flexDirection: 'column' } }),
          )}
        >
          <div className={stack({ gap: 4, flex: 1 })}>
            <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage
                id='Autogen_common.r251126.s_positionUnit_Position_size'
                values={{
                  positionUnit: positionUnit,
                }}
              />
            </p>
            <Formatted.Number
              value={positionSize.toString()}
              suffix={quoteCurrency}
              precision={quotePrecision}
              withComma
              css={{ textStyle: 'Title/S/Monospace' }}
            />
          </div>
          {unrealizedPnl != null && unrealizedPnlPct != null && (
            <div className={stack({ gap: 4, flex: 1 })}>
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage
                  id='Autogen_common.r251126.s_Unrealized_PnL_quoteCurrency'
                  values={{
                    quoteCurrency: quoteCurrency,
                  }}
                />
              </p>
              <Pnl number={unrealizedPnl} percent={unrealizedPnlPct} css={{ textStyle: 'Title/XS/Monospace' }}>
                <Pnl.Number /> <Pnl.Percent />
              </Pnl>
            </div>
          )}
          {realizedPnl != null && realizedPnlPct != null && (
            <div className={stack({ gap: 4, flex: 1 })}>
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage
                  id='Autogen_common.r251126.s_Realized_PnL_quoteCurrency'
                  values={{
                    quoteCurrency: quoteCurrency,
                  }}
                />
              </p>
              <Pnl number={realizedPnl} percent={realizedPnlPct} css={{ textStyle: 'Title/XS/Monospace' }}>
                <Pnl.Number /> <Pnl.Percent />
              </Pnl>
            </div>
          )}
        </div>
      </div>
      <hr className={divider({ orientation: 'horizontal', color: 'border.default.level-1', my: 8 })} />
      <div className={hstack({ justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 2, columnGap: 8 })}>
        <div className={hstack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>{priceLabel}</p>
          <Formatted.Number
            value={price}
            precision={pricePrecision}
            toFixed
            withComma
            css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' }}
          />
        </div>
        <Datetime
          format="MMM dd, yyyy 'at' HH:mm zz"
          value={timestamp}
          isUTC
          css={{ textStyle: 'Text/S/Regular', color: 'content.default.level-3' }}
        />
      </div>
    </div>
  );
};

function getPositionCardData(item: FeedPositionCardWithType) {
  switch (item.positionCardType) {
    case 'OPEN': {
      const card = item.positionCard;
      return {
        symbol: card.symbol,
        positionSize: Num(card.openQty).times(card.openPrice),
        price: card.openPrice,
        priceLabel: <FormattedMessage id='Autogen_common.r251126.s_Entry_price' />,
        timestamp: card.openAt,
      };
    }
    case 'HOLD': {
      const card = item.positionCard;
      return {
        symbol: card.symbol,
        positionSize: Num(card.qty).times(card.avgPrice),
        price: card.avgPrice,
        priceLabel: <FormattedMessage id='Autogen_common.r251126.s_Entry_price' />,
        timestamp: card.openAt,
        unrealizedPnl: card.unrealizedPnl,
        unrealizedPnlPct: card.unrealizedPnlPct,
      };
    }
    case 'CLOSED': {
      const card = item.positionCard;
      return {
        symbol: card.symbol,
        positionSize: Num(card.closedQty).times(card.closedPrice),
        price: card.closedPrice,
        priceLabel: 'Close price',
        timestamp: card.closedAt,
        realizedPnl: card.realizedPnl,
        realizedPnlPct: card.realizedPnlPct,
      };
    }
  }
}
